// src/services/AutoCacheService.js
import { storage } from '../firebase';
import { ref, listAll, getMetadata, getDownloadURL } from 'firebase/storage';
import DocumentService from './DocumentService';

/**
 * Service to handle automatic caching of all documents for offline use
 */
const AutoCacheService = {
  // Flag to track if initial sync is in progress
  isSyncing: false,
  
  // List of sections that have been synced
  syncedSections: new Set(),
  
  // Progress tracking
  progress: {
    total: 0,
    completed: 0,
    sections: {},
  },
  
  /**
   * Initialize the auto-cache service and start the sync
   * @returns {Promise<boolean>} - True if sync started, false otherwise
   */
  initialize: async () => {
    console.log('[AutoCacheService] Initializing auto-cache service');
    if (AutoCacheService.isSyncing) {
      console.log('[AutoCacheService] Sync already in progress');
      return false;
    }
    
    // Check if we're online
    if (!navigator.onLine) {
      console.log('[AutoCacheService] Device is offline, cannot sync');
      return false;
    }
    
    AutoCacheService.isSyncing = true;
    AutoCacheService.progress = {
      total: 0,
      completed: 0,
      sections: {},
    };
    
    try {
      // Start the synchronization process
      await AutoCacheService.syncAllContent();
      return true;
    } catch (error) {
      console.error('[AutoCacheService] Error during initialization:', error);
      AutoCacheService.isSyncing = false;
      return false;
    }
  },
  
  /**
   * Sync all content from the Firebase Storage
   * @returns {Promise<void>}
   */
  syncAllContent: async () => {
    try {
      console.log('[AutoCacheService] Starting full content sync');
      
      // 1. First get the root structure to identify all sections
      const rootFolderRef = ref(storage, 'Firebase_upload/Master Vehicle File');
      const rootResult = await listAll(rootFolderRef);
      
      // 2. Identify all sections to sync
      const sections = rootResult.prefixes.map(prefix => prefix.fullPath);
      
      // Add the root folder itself
      sections.push('Firebase_upload/Master Vehicle File');
      
      console.log(`[AutoCacheService] Found ${sections.length} sections to sync:`, sections);
      
      // 3. Update the progress tracking
      AutoCacheService.progress.total = sections.length;
      
      // 4. Sync each section
      for (const section of sections) {
        await AutoCacheService.syncSection(section);
      }
      
      console.log('[AutoCacheService] Full content sync completed');
    } catch (error) {
      console.error('[AutoCacheService] Error syncing all content:', error);
      throw error;
    } finally {
      AutoCacheService.isSyncing = false;
    }
  },
  
  /**
   * Sync a specific section and all its documents
   * @param {string} sectionPath - The path to the section
   * @returns {Promise<void>}
   */
  syncSection: async (sectionPath) => {
    try {
      console.log(`[AutoCacheService] Syncing section: ${sectionPath}`);
      
      // Setup progress tracking for this section
      AutoCacheService.progress.sections[sectionPath] = {
        total: 0,
        completed: 0,
        name: sectionPath.split('/').pop(),
      };
      
      // 1. Fetch all documents in this section
      // NOTE: Using the internal fetch method directly to avoid browser download manager
      const folderRef = ref(storage, sectionPath);
      const result = await listAll(folderRef);
      
      // Process files in this folder directly
      const documents = [];
      for (const itemRef of result.items) {
        try {
          const metadata = await getMetadata(itemRef);
          
          // Create a unique ID based on path
          const id = itemRef.fullPath.replace(/[\/\.]/g, '_');
          
          documents.push({
            id: id,
            name: itemRef.name,
            path: itemRef.fullPath,
            size: metadata.size,
            contentType: metadata.contentType,
            updated: metadata.updated,
            timeCreated: metadata.timeCreated,
            section: sectionPath
          });
        } catch (error) {
          console.error(`[AutoCacheService] Error processing item ${itemRef.name}`, error);
        }
      }
      
      // Save document metadata to IndexedDB
      if (documents.length > 0) {
        await DocumentService.saveDocumentsToIndexedDB(documents, sectionPath);
      }
      
      console.log(`[AutoCacheService] Found ${documents.length} documents in section: ${sectionPath}`);
      
      // 2. Update progress
      AutoCacheService.progress.sections[sectionPath].total = documents.length;
      
      // 3. Cache each document's content directly
      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        console.log(`[AutoCacheService] Caching document ${i + 1}/${documents.length}: ${doc.name}`);
        
        try {
          // Use a direct fetch approach that doesn't trigger browser downloads
          const fileRef = ref(storage, doc.path);
          const url = await getDownloadURL(fileRef);
          
          // Fetch with no-cors to avoid triggering downloads
          const response = await fetch(url, { 
            method: 'GET',
            cache: 'no-store',
            headers: {
              'Content-Type': 'application/octet-stream'
            },
            credentials: 'same-origin'
          });
          
          // Get the binary data
          const blob = await response.blob();
          
          // Store directly in IndexedDB using DocumentService
          await DocumentService.storeBlobInIndexedDB(doc.id, blob);
          
          // Update document metadata to mark as cached
          await DocumentService.updateDocumentCacheStatus(doc.id);
          
          // Update progress for this document
          AutoCacheService.progress.sections[sectionPath].completed++;
        } catch (docError) {
          console.error(`[AutoCacheService] Error caching document ${doc.name}:`, docError);
          // Continue with the next document even if one fails
        }
      }
      
      // 4. Mark this section as completed
      AutoCacheService.progress.completed++;
      AutoCacheService.syncedSections.add(sectionPath);
      
      console.log(`[AutoCacheService] Section ${sectionPath} sync completed`);
    } catch (error) {
      console.error(`[AutoCacheService] Error syncing section ${sectionPath}:`, error);
      
      // Still update the progress even if there was an error
      AutoCacheService.progress.completed++;
      throw error;
    }
  },
  
  /**
   * Get the current sync progress
   * @returns {Object} - The current progress
   */
  getProgress: () => {
    return {
      ...AutoCacheService.progress,
      isComplete: AutoCacheService.progress.completed >= AutoCacheService.progress.total,
      isSyncing: AutoCacheService.isSyncing,
    };
  },
  
  /**
   * Check for updates by comparing cached documents with server versions
   * @returns {Promise<Object>} - Object containing update status
   */
  checkForUpdates: async () => {
    if (!navigator.onLine) {
      return { status: 'offline', updates: [] };
    }
    
    try {
      console.log('[AutoCacheService] Checking for document updates');
      
      // 1. Get all cached documents
      const cachedDocs = await DocumentService.getOfflineDocuments();
      
      if (cachedDocs.length === 0) {
        console.log('[AutoCacheService] No cached documents found, starting full sync');
        await AutoCacheService.initialize();
        return { status: 'full-sync' };
      }
      
      // 2. Group documents by section
      const docsBySection = {};
      cachedDocs.forEach(doc => {
        if (!doc.section) return;
        
        if (!docsBySection[doc.section]) {
          docsBySection[doc.section] = [];
        }
        docsBySection[doc.section].push(doc);
      });
      
      // 3. Check each section for updates
      const updates = [];
      
      for (const section in docsBySection) {
        try {
          // Get fresh documents from the server
          const documents = [];
          
          // Fetch documents directly to avoid triggering download
          const folderRef = ref(storage, section);
          const result = await listAll(folderRef);
          
          for (const itemRef of result.items) {
            try {
              const metadata = await getMetadata(itemRef);
              
              // Create a unique ID based on path
              const id = itemRef.fullPath.replace(/[\/\.]/g, '_');
              
              documents.push({
                id: id,
                name: itemRef.name,
                path: itemRef.fullPath,
                size: metadata.size,
                contentType: metadata.contentType,
                updated: metadata.updated,
                timeCreated: metadata.timeCreated,
                section: section
              });
            } catch (error) {
              console.error(`[AutoCacheService] Error processing item ${itemRef.name}`, error);
            }
          }
          
          // Compare with cached documents
          documents.forEach(serverDoc => {
            const cachedDoc = docsBySection[section].find(d => d.id === serverDoc.id);
            
            if (!cachedDoc) {
              // This is a new document
              updates.push({
                type: 'new',
                document: serverDoc,
                section
              });
            } else if (serverDoc.updated && cachedDoc.updated && 
                      new Date(serverDoc.updated) > new Date(cachedDoc.updated)) {
              // This document has been updated
              updates.push({
                type: 'updated',
                document: serverDoc,
                section
              });
            }
          });
          
          // Check for deleted documents
          docsBySection[section].forEach(cachedDoc => {
            const serverDoc = documents.find(d => d.id === cachedDoc.id);
            if (!serverDoc) {
              updates.push({
                type: 'deleted',
                document: cachedDoc,
                section
              });
            }
          });
        } catch (sectionError) {
          console.error(`[AutoCacheService] Error checking section ${section} for updates:`, sectionError);
        }
      }
      
      console.log(`[AutoCacheService] Found ${updates.length} updates`);
      return { status: 'complete', updates };
    } catch (error) {
      console.error('[AutoCacheService] Error checking for updates:', error);
      return { status: 'error', error: error.message };
    }
  },
  
  /**
   * Apply updates found during checkForUpdates
   * @param {Array} updates - List of updates to apply
   * @returns {Promise<Object>} - Result of applying updates
   */
  applyUpdates: async (updates) => {
    if (!updates || updates.length === 0) {
      return { status: 'no-updates' };
    }
    
    try {
      console.log(`[AutoCacheService] Applying ${updates.length} updates`);
      
      const results = {
        new: 0,
        updated: 0,
        deleted: 0,
        failed: 0
      };
      
      for (const update of updates) {
        try {
          if (update.type === 'new' || update.type === 'updated') {
            // Get document content directly without triggering browser download
            const fileRef = ref(storage, update.document.path);
            const url = await getDownloadURL(fileRef);
            
            // Fetch the content
            const response = await fetch(url, { 
              method: 'GET',
              cache: 'no-store',
              headers: {
                'Content-Type': 'application/octet-stream'
              },
              credentials: 'same-origin'
            });
            
            // Get the binary data
            const blob = await response.blob();
            
            // Store in IndexedDB
            await DocumentService.storeBlobInIndexedDB(update.document.id, blob);
            
            // Update document metadata
            await DocumentService.saveDocumentsToIndexedDB([update.document], update.section);
            await DocumentService.updateDocumentCacheStatus(update.document.id);
            
            results[update.type]++;
          } else if (update.type === 'deleted') {
            // Remove from IndexedDB
            await DocumentService.removeDocumentFromCache(update.document.id);
            results.deleted++;
          }
        } catch (updateError) {
          console.error(`[AutoCacheService] Error applying update for document ${update.document.id}:`, updateError);
          results.failed++;
        }
      }
      
      console.log('[AutoCacheService] Updates applied:', results);
      return { status: 'complete', results };
    } catch (error) {
      console.error('[AutoCacheService] Error applying updates:', error);
      return { status: 'error', error: error.message };
    }
  }
};

export default AutoCacheService;